import { useEffect, useMemo, useState } from "react";
import { Download, RotateCcw } from "lucide-react";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, API } from "../lib/api";
import { Reveal } from "../lib/ui";

const AXIS = { stroke: "#3f3f46", fontSize: 11, fontFamily: "JetBrains Mono, monospace" };
const KIND_COLOUR = { "Direct economic": "#E32118", Health: "#E5A91E", Social: "#7c7c85" };
const usd = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(1)}m` : `$${Math.round(n || 0).toLocaleString()}`);
const int = (n) => Math.round(n || 0).toLocaleString();

const CONTROLS = [
  { key: "courts", label: "Futsal and 5-a-side courts", step: 5, format: (v) => `${v} courts`,
    hint: "Each court sustains about 2,400 regular players a year." },
  { key: "participation", label: "Participation rate", step: 0.05, format: (v) => `${Math.round(v * 100)}% of plan`,
    hint: "How well each facility fills against our base assumption." },
  { key: "cost", label: "Build cost", step: 0.05, format: (v) => `${Math.round(v * 100)}% of estimate`,
    hint: "Move this if you think our construction estimates are optimistic." },
  { key: "volunteers", label: "Volunteer recruitment", step: 0.1, format: (v) => `${Math.round(v * 100)}% of plan`,
    hint: "Volunteering is a priced item in both the FA and FAW studies." },
  { key: "wellbeing", label: "Wellbeing valuation", step: 0.01, format: (v) => `${Math.round(v * 100)}% of UK rate`,
    hint: "How much of the UK Green Book wellbeing value you accept for Kenya." },
];

const Slider = ({ c, value, limits, onChange }) => (
  <div data-testid={`assumption-${c.key}`}>
    <div className="flex items-baseline justify-between gap-3">
      <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400">{c.label}</p>
      <p className="mono text-xs text-ochre" data-testid={`assumption-${c.key}-value`}>{c.format(value)}</p>
    </div>
    <input
      type="range"
      min={limits[0]}
      max={limits[1]}
      step={c.step}
      value={value}
      onChange={(e) => onChange(c.key, Number(e.target.value))}
      aria-label={c.label}
      data-testid={`assumption-${c.key}-slider`}
      className="mt-3 h-1 w-full cursor-pointer appearance-none rounded-none bg-zinc-800 accent-maasai"
    />
    <p className="mt-2 text-[0.62rem] leading-relaxed text-zinc-500">{c.hint}</p>
  </div>
);

/** Jobs, training, competition and social value of the proposed facilities, with live assumption controls. */
export const GrassrootsValue = () => {
  const [d, setD] = useState(null);
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get("/grassroots-value").then((r) => {
      setD(r.data);
      setA(r.data.assumptions);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!a) return undefined;
    const id = setTimeout(() => {
      api.get("/grassroots-value", { params: a }).then((r) => setD(r.data)).catch(() => {});
    }, 180);
    return () => clearTimeout(id);
  }, [a]);

  const chart = useMemo(
    () => (d?.breakdown || []).map((b) => ({ name: b.item.split(" (")[0], value: +(b.usd / 1e6).toFixed(2), kind: b.kind })),
    [d],
  );

  if (!d || !a) return null;
  const t = d.totals;
  const limits = d.assumption_limits;
  const set = (key, value) => setA((prev) => ({ ...prev, [key]: value }));
  const dirty = JSON.stringify(a) !== JSON.stringify(d.assumption_defaults);

  return (
    <section className="mt-20 border-t border-zinc-900 pt-14" data-testid="grassroots-value">
      <Reveal>
        <p className="overline text-ochre">What the money builds</p>
        <h2 className="display-2 mt-4 max-w-4xl">
          Jobs, coaching, competition and health — priced the way England and Wales price theirs
        </h2>
        <p className="body-copy mt-6 max-w-3xl">
          The Football Association values English grassroots football at £10.16bn a year and the Football Association of
          Wales values its own game at £553m across 90,000 players. We apply the same items — participant spending,
          workforce, volunteering, healthcare savings and wellbeing — to the facilities we propose, re-priced for Kenyan
          incomes and Kenyan health spending, then add what those studies leave out for a country like Kenya: tax and VAT
          receipts, football tourism, education and skills, the services supply chain and the GDP line this sector does
          not yet have. Move the assumptions below and every number moves with you.
        </p>
      </Reveal>

      <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 lg:grid-cols-[380px_minmax(0,1fr)]">
        <div className="bg-[#0b0b0b] p-6 md:p-8" data-testid="assumption-controls">
          <div className="flex items-baseline justify-between gap-3">
            <p className="overline text-maasai">Your assumptions</p>
            {dirty ? (
              <button
                type="button"
                onClick={() => setA(d.assumption_defaults)}
                data-testid="assumption-reset"
                className="mono flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400 transition-colors hover:text-ochre"
              >
                <RotateCcw size={11} /> Reset
              </button>
            ) : null}
          </div>
          <div className="mt-7 flex flex-col gap-7">
            {CONTROLS.map((c) => (
              <Slider key={c.key} c={c} value={a[c.key]} limits={limits[c.key]} onChange={set} />
            ))}
          </div>
          <a
            href={`${API}/research-pack.pdf`}
            data-testid="prospectus-download"
            className="btn-primary mt-9 w-full justify-center"
          >
            <Download size={14} /> One-page research pack
          </a>
          <p className="mt-3 text-[0.62rem] leading-relaxed text-zinc-500">
            A research pack, generated live with today&rsquo;s population estimate and the figures on this page.
          </p>
        </div>

        <div className="grid gap-px bg-zinc-800 sm:grid-cols-2" data-testid="grassroots-headline">
          {[
            ["Annual social and economic value", usd(t.annual_social_economic_value_usd), `KSh ${(t.annual_social_economic_value_ksh / 1e9).toFixed(1)}bn a year`],
            ["Value per dollar invested", `$${t.value_per_dollar_invested}`, `Against ${usd(t.capital_usd)} of capital`],
            ["Players served", int(t.players), `$${t.value_per_player_usd} of value per player a year`],
            ["Permanent jobs", int(t.operational_jobs), `Plus ${int(t.construction_job_years)} construction person-years`],
            ["Coaching and referee places", int(t.coaching_places + t.referee_places), "Badged workforce created"],
            ["Organised matches a year", int(t.matches_per_year), `Across ${int(t.teams)} teams`],
          ].map(([label, value, note]) => (
            <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`grassroots-stat-${label.toLowerCase().replace(/\W+/g, "-")}`}>
              <p className="overline !text-zinc-500">{label}</p>
              <p className="mono mt-3 text-2xl font-bold text-ochre md:text-3xl">{value}</p>
              <p className="mt-2 text-[0.66rem] uppercase tracking-[0.14em] text-zinc-500">{note}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3" data-testid="grassroots-impacts">
        {d.impacts.map((i) => (
          <div key={i.label} className="bg-[#0b0b0b] p-6" data-testid={`grassroots-impact-${i.label.toLowerCase()}`}>
            <p className="overline text-maasai">{i.label}</p>
            <p className="mono mt-3 text-lg font-bold text-white">{i.value}</p>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">{i.detail}</p>
          </div>
        ))}
      </div>

      <div className="mt-14 border-t border-white/10 pt-10">
        <p className="overline text-ochre">Wider returns the FA and FAW studies leave out</p>
        <div className="mt-6 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3" data-testid="grassroots-wider">
          {(d.wider || []).map((i) => (
            <div key={i.label} className="bg-[#0b0b0b] p-6" data-testid={`grassroots-wider-${i.label.toLowerCase().replace(/\W+/g, "-")}`}>
              <p className="overline text-ochre">{i.label}</p>
              <p className="mono mt-3 text-lg font-bold text-white">{i.value}</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">{i.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 border border-white/10 bg-[#0b0b0b] p-6 md:p-8" data-testid="grassroots-chart">
        <p className="overline text-ochre">Annual value by item, USD millions</p>
        <p className="mt-3 text-sm text-zinc-500">
          Red is direct economic activity, ochre is healthcare saving, grey is social and wellbeing value.
        </p>
        <div className="mt-7 h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} layout="vertical" margin={{ left: 8, right: 16 }}>
              <XAxis type="number" {...AXIS} />
              <YAxis type="category" dataKey="name" width={150} {...AXIS} tick={{ fontSize: 9, fill: "#a1a1aa" }} />
              <Tooltip
                contentStyle={{ background: "#0b0b0b", border: "1px solid #E32118", borderRadius: 0, fontSize: 12 }}
                labelStyle={{ color: "#E5A91E" }}
                formatter={(v) => [`$${v}m`, "Annual value"]}
              />
              <Bar dataKey="value">
                {chart.map((c) => (
                  <Cell key={c.name} fill={KIND_COLOUR[c.kind] || "#E32118"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-10 overflow-x-auto border border-zinc-800" data-testid="grassroots-table">
        <table className="w-full min-w-[860px] border-collapse text-left">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["Facility", "Units", "Players", "Jobs", "Build person-years", "Coaching places", "Teams", "Annual value"].map((h) => (
                <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={r.item} className="border-b border-zinc-900" data-testid={`grassroots-row-${i}`}>
                <td className="px-4 py-3 text-sm text-white">{r.item}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{r.units}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.players)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.operational_jobs)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.construction_job_years)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.coaching_places)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.teams)}</td>
                <td className="mono px-4 py-3 text-sm text-ochre">{usd(r.annual_social_economic_value_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="grassroots-professional">
          <p className="overline text-ochre">The professional game</p>
          <p className="mt-3 text-sm leading-relaxed text-zinc-300">{d.professional.note}</p>
          <div className="mono mt-5 grid gap-3 text-xs text-zinc-400 sm:grid-cols-2">
            <span>{int(d.professional.matches_per_year)} league matches a year</span>
            <span>{int(d.professional.spectators_per_year)} paying spectators</span>
            <span>{d.professional.current_pros} pros today, ${d.professional.career_value_usd_m}m career value</span>
            <span>
              {d.professional.target_pros} pros modelled at ${d.professional.target_career_value_usd_m}m
            </span>
          </div>
        </div>
        <div className="border border-white/10 bg-[#0b0b0b] p-6" data-testid="grassroots-method">
          <p className="overline">Method and formula</p>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">{d.method}</p>
          <p className="mono mt-4 text-xs text-zinc-400">
            Cash factor x{d.factors.cash_factor} (GDP per capita PPP) · Health factor x{d.factors.health_factor} (health
            spend per capita) · Wellbeing at {Math.round(d.factors.wellbeing_discount * 100)}% of the UK rate
          </p>
          <div className="mt-5 flex flex-col gap-2 text-xs text-zinc-300">
            {d.sources.map((s) => (
              <a
                key={s.url}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-4 hover:text-ochre"
                data-testid="grassroots-source"
              >
                {s.source}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
